import api from '../utils/api'

/**
 * 線上商店模組 API
 *
 * 路徑對應：
 *   /api/store/shop-settings        商店設定（付款、物流、營運規則）
 *   /api/store/products/:id/variants  商品規格（SKU）
 *   /api/store/shop-orders          訂單管理
 */

// ══════════ 商店設定 ══════════

/** 取得商店設定 + 上線檢查清單 + 金流綁定狀態 */
export const getShopSettings = () => api.get('/api/store/shop-settings')

/** 更新商店設定。後端會做完整驗證，錯誤訊息在 data.errors */
export const updateShopSettings = (data) =>
  api.put('/api/store/shop-settings', data)

/**
 * 啟用／停用商店功能。
 *
 * 這支會同時設定 Business.storeEnabled 與 Store.isEnabled。
 * 前者決定 C 端店家頁要不要顯示「商品」分頁 —— 沒開的話，
 * 商品全部上架了客人也看不到，而且沒有任何錯誤訊息可查。
 */
export const setStoreEnabled = (isEnabled) =>
  api.put('/api/store', { isEnabled })

// ══════════ 商品規格（SKU）══════════

/** 取得商品目前的規格清單，以及依規格軸應有但尚未建立的組合 */
export const getProductVariants = (productId) =>
  api.get(`/api/store/products/${productId}/variants`)

/**
 * 依規格軸產生所有組合的骨架（不寫入，只回傳給前端填）。
 * 可傳入尚未儲存的 specifications 做預覽。
 */
export const generateVariants = (productId, specifications) =>
  api.post(`/api/store/products/${productId}/variants/generate`, {
    specifications
  })

/** 批次寫入規格。hasVariants=false 時會把庫存合併回商品層 */
export const updateProductVariants = (productId, data) =>
  api.put(`/api/store/products/${productId}/variants`, data)

/** 單一規格的庫存調整（add / subtract / set），會寫入庫存異動紀錄 */
export const adjustVariantStock = (productId, variantId, data) =>
  api.patch(
    `/api/store/products/${productId}/variants/${variantId}/stock`,
    data
  )

/** 商品上下架（線上商店專用，與庫存模組的 status 分開） */
export const toggleProductOnline = (productId, data) =>
  api.patch(`/api/store/products/${productId}/online-status`, data)

// ══════════ 訂單 ══════════

/** 訂單列表。status 可用逗號帶多個（例：'pending_verify,to_ship'） */
export const getShopOrders = (params) =>
  api.get('/api/store/shop-orders', { params })

/** 各狀態的訂單數，用於分頁籤徽章與 Sidebar 紅點 */
export const getShopOrderStats = () => api.get('/api/store/shop-orders/stats')

export const getShopOrderDetail = (orderId) =>
  api.get(`/api/store/shop-orders/${orderId}`)

/** 確認收款（轉帳核帳通過，或手動確認） */
export const confirmOrderPayment = (orderId, data) =>
  api.post(`/api/store/shop-orders/${orderId}/confirm-payment`, data)

/** 核帳退回。reason 必填，客戶會看到這段說明 */
export const rejectOrderPayment = (orderId, reason) =>
  api.post(`/api/store/shop-orders/${orderId}/reject-payment`, { reason })

/** 出貨。宅配的 carrier 與 trackingNo 為必填（後端會擋） */
export const shipOrder = (orderId, data) =>
  api.post(`/api/store/shop-orders/${orderId}/ship`, data)

export const completeOrder = (orderId, data) =>
  api.post(`/api/store/shop-orders/${orderId}/complete`, data)

/** 取消訂單。reason 必填，會自動回補庫存 */
export const cancelOrder = (orderId, reason) =>
  api.post(`/api/store/shop-orders/${orderId}/cancel`, { reason })

export const startRefund = (orderId, reason) =>
  api.post(`/api/store/shop-orders/${orderId}/refund-start`, { reason })

/** 退款完成，會自動回補庫存 */
export const completeRefund = (orderId, data) =>
  api.post(`/api/store/shop-orders/${orderId}/refund-complete`, data)

/** 店家內部備註，客戶看不到 */
/** 指定業績歸屬設計師（空值＝取消指定，算店家自己的收入） */
export const updateAssignedEmployee = (orderId, employeeId) =>
  api.patch(`/api/store/shop-orders/${orderId}/assigned-employee`, {
    employeeId: employeeId || null
  })

export const updateMerchantNote = (orderId, merchantNote) =>
  api.patch(`/api/store/shop-orders/${orderId}/merchant-note`, { merchantNote })

/**
 * 到店取貨核銷。
 * lookup 只查不改狀態，讓店家先確認訂單內容再放貨；confirm 才真的結案。
 */
export const lookupPickup = (code) =>
  api.get('/api/store/shop-orders/pickup/lookup', { params: { code } })

export const confirmPickup = (code, via = 'manual') =>
  api.post('/api/store/shop-orders/pickup/confirm', { code, via })

// ══════════ 共用常數 ══════════

/** 訂單狀態顯示設定。與後端 utils/shopOrderStateMachine.js 的 STATUS_LABEL 對齊 */
export const ORDER_STATUS_META = {
  pending_payment: { label: '待付款', color: 'orange' },
  pending_verify: { label: '待核帳', color: 'gold' },
  to_ship: { label: '待出貨', color: 'blue' },
  shipped: { label: '已出貨', color: 'cyan' },
  completed: { label: '已完成', color: 'green' },
  cancelled: { label: '已取消', color: 'default' },
  refunding: { label: '退款處理中', color: 'volcano' },
  refunded: { label: '已退款', color: 'purple' }
}

export const PAYMENT_METHOD_LABEL = {
  online: '線上刷卡',
  bank_transfer: '轉帳匯款',
  cod: '貨到付款'
}

export const DELIVERY_METHOD_LABEL = {
  home_delivery: '宅配到府',
  store_pickup: '到店取貨'
}

export const PAYMENT_STATUS_META = {
  unpaid: { label: '未付款', color: 'default' },
  pending_verify: { label: '待核帳', color: 'gold' },
  paid: { label: '已付款', color: 'green' },
  refunding: { label: '退款中', color: 'volcano' },
  refunded: { label: '已退款', color: 'purple' },
  failed: { label: '付款失敗', color: 'red' }
}
